import { useState } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/ProductsShowcase.css';
import ShareableHeading from './ShareableHeading';

type Locale = 'hi' | 'en';
type CopyMap = Record<Locale, string>;
type Panel = 'grantha' | 'abhyaas' | 'upkaran';
type OfferingKind = 'book' | 'tool' | 'game';

const DIMENSION_LABELS: Record<string, CopyMap> = {
  food:      { hi: 'भोजन',    en: 'Food' },
  clothing:  { hi: 'वस्त्र',   en: 'Clothing' },
  housing:   { hi: 'आवास',    en: 'Housing' },
  education: { hi: 'शिक्षा',  en: 'Education' },
  health:    { hi: 'स्वास्थ्य', en: 'Health' },
  transport: { hi: 'यातायात', en: 'Transport' },
  festival:  { hi: 'उत्सव',   en: 'Festival' },
  tradition: { hi: 'त्योहार', en: 'Tradition' },
  defense:   { hi: 'रक्षा',   en: 'Defense' },
  service:   { hi: 'सेवा',    en: 'Service' },
  justice:   { hi: 'न्याय',   en: 'Justice' },
  marriage:  { hi: 'विवाह',   en: 'Marriage' },
};

interface Product {
  id: string;
  title: CopyMap;
  panel: Panel;
  kind?: OfferingKind;
  dimension: string;
  status: 'available' | 'pending';
  price: CopyMap;
  isFree?: boolean;
  description: CopyMap;
  imageUrl: string;
  buyLink?: string;
  internalPath?: string;
  cta?: CopyMap;
  publishedAt?: string; // ISO date — used to sort grantha panel latest-first
  isLatest?: boolean;   // badge override
}

const PRODUCTS: Product[] = [
  // ── ग्रंथ ─ latest first ──────────────────────────────────────────────────
  {
    id: 'arya-rahasyamayi-guha-book',
    panel: 'grantha',
    publishedAt: '2026-10-03',
    isLatest: true,
    title: { hi: 'आर्य और रहस्यमयी गुहा — अमरकोश विशेष्यनिघ्न', en: 'Ārya and the Mysterious Cave — Amarakosha Viśeṣyanighna' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'आर्य की तृतीय चित्र-कथा। ३–८ वर्ष के बालकों के लिए पर्वतीय गुहा-अन्वेषण — मुनि का रहस्यमय चित्र, मित्र की सहायता और यक्ष का परीक्षण। अमरकोश विशेष्यनिघ्नवर्ग श्लोक २०३७–२०४० के सत्रह शक्ति-शब्द: जैवातृकः, आयुष्मान्, शास्त्रवित्… उन्मनाः। २४ पृष्ठ। PDF।',
      en: 'Ārya’s third picture-book quest for boys aged 3–8 — a mysterious map, a friend’s help, and a yakṣa’s test inside a mountain cave. Seventeen power-words from Amarakosha Viśeṣyanighna ślokas 2037–2040: jaivātṛkaḥ, āyuṣmān, śāstravit… unmanāḥ. 24 pages. PDF.',
    },
    imageUrl: '/images/cover_arya_rahasyamayi_guha.jpg',
    internalPath: '/products/arya-rahasyamayi-guha',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'arya-svarnamanjusha-book',
    panel: 'grantha',
    publishedAt: '2026-08-19',
    title: { hi: 'आर्यः स्वर्णमञ्जूषा च — अमरकोश विशेष्यनिघ्न', en: 'Āryaḥ Svarṇamañjūṣā ca — Amarakosha Viśeṣyanighna' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'आर्यः लघुखगः च की उत्तर-कथा। ३–८ वर्ष के बालकों के लिए चित्र-कथा — नन्दनग्राम में एक बन्द स्वर्ण-पेटिका मिली। अमरकोश विशेष्यनिघ्नवर्ग श्लोक २०३३–२०३६ के पन्द्रह शक्ति-शब्द: सांशयिकः, कृती, वैज्ञानिकः, वदान्यः… बहुप्रदः। २० पृष्ठ, बड़ी तस्वीरें, छोटी हिन्दी पंक्तियाँ। PDF।',
      en: 'Sequel to Āryaḥ Laghukhagaḥ ca. Picture-book quest for boys aged 3–8 — a locked golden casket in Nandanagrāma. Fifteen power-words from Amarakosha Viśeṣyanighna ślokas 2033–2036: sāṃśayikaḥ, kṛtī, vaijñānikaḥ, vadānyaḥ… bahupradaḥ. 20 pages, big paintings, short Hindi lines. PDF.',
    },
    imageUrl: '/images/cover_arya_svarnamanjusha.jpg',
    internalPath: '/products/arya-svarnamanjusha',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'arya-laghukhaga-book',
    panel: 'grantha',
    publishedAt: '2026-08-18',
    title: { hi: 'आर्यः लघुखगः च — अमरकोश विशेष्यनिघ्न', en: 'Āryaḥ Laghukhagaḥ ca — Amarakosha Viśeṣyanighna' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: '३–८ वर्ष के बालकों के लिए चित्र-कथा — नंदनवन में आर्य एक नन्हे पक्षी को जाल से छुड़ाता है। अमरकोश विशेष्यनिघ्नवर्ग श्लोक २०३०–२०३२ के पन्द्रह शक्ति-शब्द: सुकृती, पुण्यवान्, महेच्छः… धन्यः। २० पृष्ठ, बड़ी तस्वीरें, छोटी हिन्दी पंक्तियाँ। PDF।',
      en: 'Picture-book rescue quest for boys aged 3–8 — Ārya frees a small bird from a net in Nandanavana. Fifteen power-words from Amarakosha Viśeṣyanighna ślokas 2030–2032: sukṛtī, puṇyavān, mahecchaḥ… dhanyaḥ. 20 pages, big paintings, short Hindi lines. PDF.',
    },
    imageUrl: '/images/cover_arya_laghukhaga.jpg',
    internalPath: '/products/arya-laghukhaga',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'panchakosha-workbook',
    panel: 'grantha',
    publishedAt: '2026-07-23',
    isLatest: true,
    title: { hi: 'पंचकोश विकास कार्यपुस्तिका', en: 'Panchakosha Vikāsa Workbook' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'तैत्तिरीय उपनिषद् पर आधारित परिवार-अभ्यास कार्यपुस्तिका — ३–८ वर्ष के बच्चों एवं माता-पिता के लिए। पाँच कोश: अन्नमय, प्राणमय, मनोमय, विज्ञानमय, आनन्दमय। ३९ संयुक्त अभ्यास, २–३ परिवारों के समूह के लिए, चित्र-स्थान एवं विषय-सूची सहित। PDF।',
      en: 'Family exercise workbook based on the Taittirīya Upaniṣad — for children (ages 3–8) and parents. Five sheaths: Annamaya, Prāṇamaya, Manomaya, Vijñānamaya, Ānandamaya. 39 joint exercises, designed for groups of 2–3 families, with drawing slots and a table of contents. PDF.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1784859681/manomaya_1_hzbebb.png',
    internalPath: '/products/panchakosha-workbook',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'laukika-nyaya-book',
    panel: 'grantha',
    publishedAt: '2026-08-07',
    title: { hi: 'लौकिक न्याय कोश — ७४३ भारतीय न्याय-वाक्य', en: 'Laukika Nyāya Kosha — 743 Indian Folk Maxims' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'संस्कृत के ७४३ लौकिक न्याय-वाक्य — शुद्ध हिन्दी अर्थ, अंग्रेज़ी व्याख्या एवं शास्त्र-प्रयोग (वेदान्त · न्याय · मीमांसा · व्याकरण · धर्मशास्त्र · आयुर्वेद) सहित। वर्णक्रम-सूची और सारणी-प्रारूप। स्रोत: ambuda.org · संपादक: रामसेवक। ३८१ पृष्ठ। PDF।',
      en: '743 Sanskrit folk maxims (Laukika Nyāyas) — with pure Hindi meanings, English explanations, and Shastra usage across Vedānta, Nyāya, Mīmāṃsā, Vyākaraṇa, Dharmaśāstra, and Āyurveda. Alphabetical index and table layout. Source: ambuda.org · Editor: Rāmasevaka. 381 pages. PDF.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778513728/cover_nrivarg_elhtq0.png',
    internalPath: '/products/laukika-nyaya',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'parashar-geeta-book',
    panel: 'grantha',
    publishedAt: '2026-06-14',
    title: { hi: 'पराशर-गीता — जयनगर से धर्मशाला · दो भाइयों की यात्रा', en: 'Parashara Gītā — Jayanagara to Dharamshala · A Road-Trip Dialogue' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'महाभारत शान्तिपर्व (अध्याय २९०–२९८) — राजा जनक के वंशज दो क्षत्रिय भाई जयनगर (बिहार) से धर्मशाला (हिमाचल) की सड़क यात्रा में पराशर-जनक संवाद के ~२७० श्लोक जीते हैं। ६८ दृश्य, पाणिनीय व्याकरण, यास्क निरुक्ति, शुद्ध देवनागरी। PDF।',
      en: "Mahabharata Shantiparva (Ch. 290–298) — two Kshatriya brothers of Janaka's lineage journey from Jayanagara (Bihar) to Dharamshala (HP), living the ~270 shlokas of the Parashara-Janaka dialogue. 68 scenes, Pāṇinian grammar, Yāska Nirukta, pure Devanāgarī. PDF.",
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1781605503/cover_apeli6.png',
    internalPath: '/products/parashar-geeta',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'vaishyavarg-khand1-book',
    panel: 'grantha',
    publishedAt: '2026-05-18',
    title: { hi: 'गोवर्धन — खण्ड १ · बीज · वैश्यवर्ग — अमरकोश', en: 'Govardhana Vol. 1 · Bīja · Vaishyavarga — Amarakosha' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'आर्यन् वर्मा की यात्रा — बेंगलुरु से धर्मशाला, पण्डित सूर्यदेव शास्त्री से भेंट और गुरुकुल की नींव। वेद लक्षणा गौ वंश (Bos Indicus), पञ्चगव्य और गुरु-शिष्य परंपरा। अमरकोश वैश्यवर्ग श्लोक १–६। काँगड़ा पहाड़ी लघुचित्र। PDF।',
      en: "Āryan Vermā's journey — Bangalore to Dharamshala, meeting Pandit Suryadev Shastri, founding the Gurukul. Veda Lakṣaṇā Gau Vaṃśa (Bos Indicus), pañcagavya and Guru-Śiṣya tradition. Amarakosha Vaishyavarga shlokas 1–6. Kangra Pahari miniatures. PDF.",
    },
    imageUrl: '/images/cover_vaishyavarg_khand1.jpg',
    internalPath: '/products/vaishyavarg-khand1',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'vaishyavarg-khand2-book',
    panel: 'grantha',
    publishedAt: '2026-05-18',
    title: { hi: 'गोवर्धन — खण्ड २ · अङ्कुर · वैश्यवर्ग — अमरकोश', en: 'Govardhana Vol. 2 · Aṅkura · Vaishyavarga — Amarakosha' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'गोशाला की स्थापना, पञ्चगव्य उद्यम, प्रौद्योगिकी मंच और ₹१ करोड़ की यात्रा। वेद लक्षणा गौ वंश के उत्पाद और जैविक कृषि। अमरकोश वैश्यवर्ग श्लोक ७–११। काँगड़ा पहाड़ी लघुचित्र। PDF।',
      en: 'Gośālā founding, pañcagavya enterprise, tech platform and the ₹1 crore journey. Bos Indicus products and organic farming. Amarakosha Vaishyavarga shlokas 7–11. Kangra Pahari miniatures. PDF.',
    },
    imageUrl: '/images/cover_vaishyavarg_khand2.jpg',
    internalPath: '/products/vaishyavarg-khand2',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'vaishyavarg-khand3-book',
    panel: 'grantha',
    publishedAt: '2026-05-18',
    title: { hi: 'गोवर्धन — खण्ड ३ · फल · वैश्यवर्ग — अमरकोश', en: 'Govardhana Vol. 3 · Phala · Vaishyavarga — Amarakosha' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: '₹१०,००० करोड़ का वैश्विक Bos Indicus उद्यम, नेतृत्व शिक्षा और गुरु-शिष्य परंपरा का पूर्ण फल। अमरकोश वैश्यवर्ग श्लोक १२–१८ एवं गीता १८.४४। काँगड़ा पहाड़ी लघुचित्र। PDF।',
      en: '₹10,000 crore global Bos Indicus enterprise, leadership education and the full fruition of Guru-Śiṣya tradition. Amarakosha Vaishyavarga shlokas 12–18 + Gītā 18.44. Kangra Pahari miniatures. PDF.',
    },
    imageUrl: '/images/cover_vaishyavarg_khand3.jpg',
    internalPath: '/products/vaishyavarg-khand3',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'kshatriyavarg-book',
    panel: 'grantha',
    publishedAt: '2026-05-13',
    title: { hi: 'क्षत्रियवर्ग — अमरकोश · वेद लक्षणा गौ वंश संवाद', en: 'Kṣatriyavarga — Amarakosha · Veda Lakṣaṇā Gau Vaṃśa Dialogue' },
    dimension: 'justice',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'अमरकोश क्षत्रियवर्ग के श्लोकों पर आधारित — राजा, चक्रवर्ती, सेना और दुर्ग की कथा — गौरी, नन्दिनी, वृषभराज और धवली का हिन्दी-संस्कृत संवाद। पाणिनीय कारक-विश्लेषण, मुगल-राजपूत लघुचित्र। PDF।',
      en: 'Based on shlokas of Amarakosha Kṣatriyavarga — story of Rājā, Cakravartī, army and fort — Hindi-Sanskrit dialogues of Gaurī, Nandinī, Vṛṣabharāja and Dhavalī. Pāṇinian kāraka analysis, Mughal-Rajput miniatures. PDF.',
    },
    imageUrl: 'https://storage.googleapis.com/raajadharma-books/images/cover_kshatriyavarg.png',
    internalPath: '/products/kshatriyavarg',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'nrivarg-book',
    panel: 'grantha',
    publishedAt: '2026-05-11',
    title: { hi: 'नृवर्ग — अमरकोश · रक्कड़ गाँव संवाद', en: 'Nṛvarga — Amarakosha · Rakkad Village Dialogue' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'अमरकोश नृवर्ग के १०१ श्लोकों पर आधारित — रक्कड़ गाँव (धर्मशाला) की धेनु माँ, अजा माँ, बछिया और मेमने का हिन्दी-संस्कृत संवाद। पाणिनीय कारक-विश्लेषण, यास्क निरुक्ति, मुगल-हिमाचली लघुचित्र। PDF।',
      en: 'Based on 101 shlokas of Amarakosha Nṛvarga — Hindi-Sanskrit dialogues of a cow, goat and their calves in Rakkad village, Dharamshala. Pāṇinian kāraka analysis, Yāska Nirukta, Mughal-Himachali miniatures. PDF.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778513728/cover_nrivarg_elhtq0.png',
    internalPath: '/products/nrivarg',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'singh-amara-book',
    panel: 'grantha',
    publishedAt: '2025-04-25',
    title: { hi: 'सिंह की महिमाएँ — अमरकोश', en: 'Glory of Lions — Amarakosha' },
    dimension: 'justice',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'अमरकोश के सिंहादिवर्ग पर आधारित संस्कृत चित्र-पुस्तक — गिर वन, महाभारत श्लोक, और विश्व-साहित्य। PDF + EPUB।',
      en: "Illustrated children's book on the Simhādi Varga of the Amarakosha — Gir forest, Mahabharata verses, and world literature. PDF + EPUB.",
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1777591906/Chitrakaya_qb14s7.png',
    internalPath: '/products/singh-amara',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'geedh-book',
    panel: 'grantha',
    publishedAt: '2025-04-20',
    title: { hi: 'गीधकी-आँख — द्रोणगुरुकुल · एकलव्य · पक्षी-परीक्षा', en: 'Gīdhakī-Āṃkha — The Eye of the Kite' },
    dimension: 'defense',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'श्रीमहाभारत आदिपर्व के एकसौ इकतीसवें अध्याय पर आधारित — अर्जुन की एकाग्रता, एकलव्य की गुरुदक्षिणा — २८ दृश्य, ८० श्लोक, राजपूत-मेवाड़ लघुचित्र। PDF।',
      en: "Based on Mahābhārata Ādiparvam Ch. 131 — Arjuna's focus, Ekalavya's gurudakṣiṇā — 28 scenes, 80 shlokas, Rajput-Mewar miniature illustrations. PDF.",
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778510877/cover_geedh_qrvtp5.png',
    internalPath: '/products/geedh',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'maataa-pitaa-guru-book',
    panel: 'grantha',
    publishedAt: '2026-06-12',
    isLatest: true,
    title: { hi: 'माता-पिता-और-गुरु — शिष्य-संवाद · गुरु-प्राप्ति के उपाय', en: 'Maataa-Pitaa-Aur-Guru — Student Dialogue · Means of Finding the Right Guru' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'श्रीमहाभारत शान्तिपर्व (अष्टाधिकशततमोऽध्याय) पर आधारित — दो शिष्य (चेतन और धीर) माता-पिता-गुरु के माहात्म्य और सत्गुरु खोजने के उपाय सीखते हैं। सरल संस्कृत वाक्य (८ पद से कम), हिन्दी अनुवाद, देवनागरी, पाणिनीय विश्लेषण, राजपूत-मेवाड़ लघुचित्र। PDF।',
      en: 'Based on Mahābhārata Śāntiparva (Ch. 108) — two students (Chetana & Dhīra) explore the glory of mother-father-guru and the means to find the right Guru. Simple Sanskrit sentences (≤8 words), Hindi translations, Devanāgarī, Pāṇinian notes, Rajput-Mewar miniatures. PDF.',
    },
    imageUrl: 'https://storage.googleapis.com/raajadharma-books/images/cover_maataa_pitaa_guru.png',
    internalPath: '/products/maataa-pitaa-guru',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'vyaghra-valmiki-book',
    panel: 'grantha',
    publishedAt: '2025-04-10',
    title: { hi: 'व्याघ्र की महिमाएँ — अमरकोश', en: 'Glory of Tigers — Amarakosha' },
    dimension: 'defense',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'अमरकोश के सिंहादिवर्ग पर आधारित संस्कृत पुस्तक — वाल्मीकि राष्ट्रीय उद्यान की पृष्ठभूमि में भाई-बहन के हिंदी-संस्कृत संवाद, मधुबनी चित्र, पाणिनि निरुक्त। PDF।',
      en: 'Sanskrit book on the tiger names of Amarakosha — bilingual Hindi–Sanskrit sibling dialogues set in Valmiki National Park, Madhubani illustrations, Pāṇini Nirukta analysis. PDF.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1777591349/cover-tiger__upscayl_2x_upscayl-lite-4x_uskukx.png',
    internalPath: '/products/vyaghra-valmiki',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'saagar-manthan-book',
    panel: 'grantha',
    publishedAt: '2025-04-01',
    title: { hi: 'सागर-मंथन — क्षीरसागर की महाकथा', en: 'Sāgara-Manthana — The Churning of the Ocean' },
    dimension: 'health',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'श्रीमहाभारत आदिपर्व के अष्टादशाध्याय में वर्णित अमृत-मन्थन — १५ दृश्यों में, संस्कृत श्लोक, हिन्दी भावार्थ, पहाड़ी लघुचित्र। PDF।',
      en: 'The churning of the cosmic ocean from Mahābhārata Ādiparvam Ch.18 — 15 illustrated scenes, Sanskrit shlokas, Hindi commentary, Pahadi miniature paintings. PDF.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778510960/cover_saagar_manthan_tijhhx.png',
    internalPath: '/products/saagar-manthan',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'nishedha-madira-book',
    panel: 'grantha',
    publishedAt: '2025-03-15',
    title: { hi: 'निषेध-मदिरा — उमा-महेश संवाद', en: 'Niṣedha-Madirā — Umā-Maheśa Dialogue' },
    dimension: 'food',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'भगवान् महेश द्वारा देवी उमा को मदिरा-पान के दोषों पर पौराणिक संस्कृत संवाद — १३ श्लोक, हिंदी भावार्थ, पहाड़ी लघुचित्र। PDF।',
      en: 'Puranic Sanskrit dialogue between Lord Shiva and Goddess Parvati on the harms of alcohol — 13 shlokas, Hindi commentary, Pahadi miniature illustrations. PDF.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778513650/cover_nishedha_madira_y84b0u.png',
    internalPath: '/products/nishedha-madira',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'vaanara-samvad-book',
    panel: 'grantha',
    publishedAt: '2025-02-20',
    title: { hi: 'वानर-संवाद — अमरकोश', en: 'Vānara Saṃvāda — Amarakosha' },
    dimension: 'service',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'अमरकोश के सिंहादिवर्ग के श्लोक ७-८ पर आधारित संस्कृत पुस्तक — दस वानर-नामों पर तुलसीदास और भगवान् हनुमान के हिंदी-संस्कृत संवाद, दरभंगा घाट पर प्रातःकाल। PDF।',
      en: 'Sanskrit book on the 10 monkey names from Shlokas 7-8 of the Amarakosha Simhadi Varga — bilingual Hindi–Sanskrit dialogues between Tulsidas and Lord Hanuman, set at Darbhanga Ghat at dawn. PDF.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1777882614/back-cover_den5m3.png',
    internalPath: '/products/vaanara-samvad',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'varaaha-samvad-book',
    panel: 'grantha',
    publishedAt: '2025-02-10',
    title: { hi: 'वाराह-संवाद — अमरकोश', en: 'Varāha Saṃvāda — Amarakosha' },
    dimension: 'tradition',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'अमरकोश के सिंहादिवर्ग पर आधारित संस्कृत पुस्तक — वराह के १२ नामों पर हनुमत्-भक्त और आतंककारी के हिंदी-संस्कृत संवाद, राजपूत-पहाड़ी लघुचित्र। PDF।',
      en: 'Sanskrit book on the 12 boar names of Amarakosha — bilingual Hindi–Sanskrit dialogues between a Hanuman devotee and a terrorist, Rajput-Pahari miniature illustrations. PDF.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1777734567/varaha-cover_upscayl_2x_upscayl-lite-4x_ycak4r.png',
    internalPath: '/products/varaaha-samvad',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'hanuman-bahuk-book',
    panel: 'grantha',
    publishedAt: '2025-01-28',
    title: { hi: 'हनुमान बाहुक — तुलसीदास', en: 'Hanumān Bāhuk — Tulsidas' },
    dimension: 'service',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'गोस्वामी तुलसीदास रचित ४४ अवधी पदों की भक्ति-स्तुति — हिंदी भावार्थ और राजपूत-पहाड़ी लघुचित्रों सहित। US Letter लैंडस्केप PDF।',
      en: '44 Awadhi devotional verses by Goswami Tulsidas — with Hindi meaning (bhavaartha) and Rajput-Pahari miniature illustrations. US Letter landscape PDF.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1778513880/verse_01_sindhu_taran_tkg3qb.png',
    internalPath: '/products/hanuman-bahuk',
    cta: { hi: 'पढ़ें', en: 'Read' },
  },
  {
    id: 'book-vanyagramya',
    panel: 'grantha',
    publishedAt: '2025-01-15',
    title: { hi: 'वन्यग्राम्य पशु', en: 'Wild and Village Animals' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'मनुष्य एक ग्राम्य पशु है और शिव पशुपति हैं। अमरकोश सिंहादिवर्ग पर आधारित संस्कृत चित्र-पुस्तक।',
      en: 'Human life is rooted in the village order, and Shiva is the lord of all beings. An illustrated Sanskrit book based on Amarakosha Simhadi Varga.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775694559/phpThumb_generated_thumbnail_upscayl_2x_upscayl-standard-4x_mfjwge.png',
    internalPath: '/products/vanyagraamya-pashu',
    cta: { hi: 'पढ़ें', en: 'Read now' },
  },

  // ── अभ्यास ────────────────────────────────────────────────────────────────
  {
    id: 'rajdharma-module',
    panel: 'abhyaas',
    title: { hi: 'राजधर्मानुशासन योग — धनुर्वेद अभ्यास', en: 'Rajdharma discipline — Dhanurveda practice' },
    dimension: 'defense',
    status: 'available',
    price: { hi: '₹२,७०० (अग्रिम ₹५००)', en: '₹2,700 (advance ₹500)' },
    description: {
      hi: 'धर्मशाला, हि.प्र. में पारम्परिक काष्ठ-धनुष अभ्यास — अर्जुन की धनुर्विद्या, महाभारत संस्कृत पाठ, एकाग्रता साधना। १५ दिन आवासीय कार्यक्रम।',
      en: 'Traditional wooden-bow practice in Dharamshala, Himachal Pradesh — Arjuna-style archery, Mahabharata Sanskrit recitation, and concentration training. 15-day residential program.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1775926720/Gemini_ijd695ijd695ijd6_upscayl_1x_upscayl-standard-4x_oxiptq.png',
    internalPath: '/rajdharma/dhanurveda',
    cta: { hi: 'अधिक जानें', en: 'Learn more' },
  },
  {
    id: 'sanskrit-course',
    panel: 'abhyaas',
    title: { hi: 'संस्कृत संवाद अभ्यास – ६ माह कोर्स', en: 'Sanskrit Conversation Practice — 6 month course' },
    dimension: 'education',
    status: 'available',
    price: { hi: '₹९,०००', en: '₹9,000' },
    description: {
      hi: 'पूर्ण राजधर्मानुशासन + योगसूत्र वार्तालाप।',
      en: 'Complete Rajdharma discipline with Yoga Sutra conversation practice.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775693711/garuda_ajlfel.png',
  },
  {
    id: 'raam-sevak-youtube',
    panel: 'abhyaas',
    title: { hi: 'राम सेवक — वैदिक कथाएँ (YouTube)', en: 'Ram Sevak — Vedic Stories (YouTube)' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: 'वैदिक दृष्टिकोण से भारतीय कथाएँ — रामायण, महाभारत आदि के हिन्दी भाषांतरण। प्रत्येक कथा में सदा-प्रासंगिक नीतिपरक उद्धरण।',
      en: 'Indian stories from a Vedic perspective — narrated from Gita Press renditions of Ramayana, Mahabharata, with timeless ethical quotations.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775693711/garuda_ajlfel.png',
    buyLink: 'https://www.youtube.com/@raam_sevak',
    cta: { hi: 'YouTube पर देखें', en: 'Watch on YouTube' },
  },
  {
    id: 'engineering-course',
    panel: 'abhyaas',
    title: { hi: 'वेदधारित संस्कृत ज्ञान से यथायोग्य धन उपार्जन', en: 'Appropriate earning through Vedic Sanskrit knowledge' },
    dimension: 'food',
    status: 'pending',
    price: { hi: '₹९,००० (pre-order)', en: '₹9,000 (pre-order)' },
    description: {
      hi: 'जल्द आ रहा है – ९ एकड़ वर्टिकल फार्मिंग से।',
      en: 'Coming soon — from a 9-acre vertical farming initiative.',
    },
    imageUrl: 'https://picsum.photos/id/301/400/300',
  },

  // ── उपकरण ────────────────────────────────────────────────────────────────
  {
    id: 'navagrah-chakra-game',
    panel: 'upkaran',
    kind: 'game',
    publishedAt: '2026-04-11',
    title: { hi: 'नवग्रह चक्र — योगसूत्र खेल', en: 'Navagrah Chakra — Yoga Sutra Game' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: '९ ग्रहों के वृत्त में योगसूत्र के पदों का ज्ञान — पद-अर्थ, संधि, धातु, निरुक्त, पाणिनि सूत्र। ४,५४० प्रश्न-पत्र।',
      en: 'A learning game for the Yoga Sutra terms across nine planetary cycles — word meaning, sandhi, roots, nirukta, and Pāṇini sūtra. 4,540 question cards.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775693711/garuda_ajlfel.png',
    internalPath: '/curriculum/navagrahchakra',
    cta: { hi: 'खेलें अभी', en: 'Play now' },
  },
  {
    id: 'sanskrit-lakar-game',
    panel: 'upkaran',
    kind: 'game',
    publishedAt: '2026-04-12',
    title: { hi: 'संस्कृत लकार खेल', en: 'Sanskrit Verb Game' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: '१० लकार × ५७ धातु — महाभारत श्लोकों पर आधारित संवादी संस्कृत क्रिया-रूप अभ्यास खेल। टोकन अर्थव्यवस्था के साथ स्वमूल्यांकन।',
      en: '10 tenses × 57 roots — a conversational Sanskrit verb-form practice game based on Mahabharata verses, with token-based self-assessment.',
    },
    imageUrl: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775693711/garuda_ajlfel.png',
    internalPath: '/curriculum/spokensanskritgame',
    cta: { hi: 'खेलें अभी', en: 'Play now' },
  },
  {
    id: 'amarkosha-fpp-game',
    panel: 'upkaran',
    kind: 'game',
    publishedAt: '2026-09-09',
    title: { hi: 'एक में नौ', en: 'One in Nine' },
    dimension: 'education',
    status: 'available',
    price: { hi: 'निःशुल्क', en: 'Free' },
    isFree: true,
    description: {
      hi: '७३ पत्र · प्रत्येक पर ९ चिह्न। किन्हीं दो पत्रों पर ठीक एक समान शब्द। चिह्न छुएँ, संस्कृत बोलें, वाक्य बनाएँ। अलग फ़ोन पर कक्ष-लिंक से खेलें।',
      en: '73 cards · 9 symbols each. Any two cards share exactly one word. Tap it, speak Sanskrit, form a sentence. Play across phones with a room link.',
    },
    imageUrl: '/games/amarkosha-fpp/cards/card_001.svg',
    internalPath: '/products/tools/game',
    cta: { hi: 'खेलें अभी', en: 'Play now' },
  },
];

export interface LatestOffering {
  kind: OfferingKind;
  id: string;
  title: CopyMap;
  description: CopyMap;
  imageUrl: string;
  path: string;
  publishedAt?: string;
}

function offeringKind(product: Product): OfferingKind | null {
  if (product.kind) return product.kind;
  if (product.panel === 'grantha') return 'book';
  if (product.panel === 'upkaran') return 'game';
  return null;
}

/** Newest available book, tool, and game — one of each. */
export function latestOfferings(): LatestOffering[] {
  const order: OfferingKind[] = ['book', 'tool', 'game'];
  return order.flatMap((kind) => {
    const newest = PRODUCTS
      .filter((product) => offeringKind(product) === kind && product.status === 'available' && product.internalPath)
      .sort((a, b) => (b.publishedAt ?? '').localeCompare(a.publishedAt ?? ''))[0];
    if (!newest?.internalPath) return [];
    return [{
      kind,
      id: newest.id,
      title: newest.title,
      description: newest.description,
      imageUrl: newest.imageUrl,
      path: newest.internalPath,
      publishedAt: newest.publishedAt,
    }];
  });
}

const PANELS: { id: Panel; hi: string; en: string; icon: string }[] = [
  { id: 'grantha',  hi: 'ग्रंथ',   en: 'Books',       icon: '📖' },
  { id: 'abhyaas',  hi: 'अभ्यास',  en: 'Experiences', icon: '🎯' },
  { id: 'upkaran',  hi: 'उपकरण',   en: 'Tools',       icon: '🎮' },
];

const PAGE_COPY: Record<Locale, { title: string; subtitle: string }> = {
  hi: { title: 'उत्पाद प्रदर्शन', subtitle: 'धर्मतः उत्पादित — ग्रंथ · अभ्यास · उपकरण' },
  en: { title: 'Products',        subtitle: 'Dharma-produced offerings — Books · Experiences · Tools' },
};

function pickCopy(value: CopyMap, locale: string): string {
  return value[locale as Locale] ?? value.hi;
}

export default function ProductsShowcase({ onNavigate }: { onNavigate?: (path: string) => void }) {
  const { t, locale } = useGlossary();
  const [activePanel, setActivePanel] = useState<Panel>('grantha');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const activeLocale: Locale = locale === 'en' ? 'en' : 'hi';

  const handleShare = async (product: Product) => {
    if (!product.internalPath) return;
    const url = `${window.location.origin}${product.internalPath}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: pickCopy(product.title, activeLocale),
          text: pickCopy(product.description, activeLocale),
          url,
        });
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopiedId(product.id);
      setTimeout(() => setCopiedId(c => c === product.id ? null : c), 2200);
    }
  };

  const highlightedIds = new Set(latestOfferings().map((item) => item.id));

  const booksOrdered = PRODUCTS
    .filter(p => p.panel === 'grantha')
    .sort((a, b) => (b.publishedAt ?? '').localeCompare(a.publishedAt ?? ''));

  const panelItems: Record<Panel, Product[]> = {
    grantha: booksOrdered,
    abhyaas: PRODUCTS.filter(p => p.panel === 'abhyaas'),
    upkaran: PRODUCTS.filter(p => p.panel === 'upkaran'),
  };

  const openProduct = (product: Product) => {
    if (product.status !== 'available') return;
    if (product.internalPath) {
      onNavigate?.(product.internalPath);
      return;
    }
    if (product.buyLink) {
      window.open(product.buyLink, '_blank', 'noopener,noreferrer');
    }
  };

  const renderCard = (product: Product) => {
    const label = DIMENSION_LABELS[product.dimension]?.[activeLocale] ?? product.dimension;
    const canOpen = product.status === 'available' && !!(product.internalPath || product.buyLink);
    return (
      <div
        key={product.id}
        className={`ps-card ${product.status}${canOpen ? ' ps-card-clickable' : ''}`}
        onClick={canOpen ? () => openProduct(product) : undefined}
      >
        <div className="ps-card-image-wrap">
          {highlightedIds.has(product.id) && <span className="ps-latest-badge">🆕 नवीनतम</span>}
          <img
            src={product.imageUrl}
            alt={pickCopy(product.title, activeLocale)}
            className="ps-card-image"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://picsum.photos/id/1015/400/300';
            }}
          />
        </div>
        <div className="ps-card-body">
          <span className="ps-hashtag">#{label}</span>
          {product.kind === 'tool' && (
            <span className="ps-kind-chip">{activeLocale === 'en' ? 'Tool' : 'उपकरण'}</span>
          )}
          {product.kind === 'game' && (
            <span className="ps-kind-chip">{activeLocale === 'en' ? 'Game' : 'खेल'}</span>
          )}
          <h4 className="ps-card-title">{pickCopy(product.title, activeLocale)}</h4>
          <p className="ps-card-desc">{pickCopy(product.description, activeLocale)}</p>
          <div className="ps-card-footer">
            <span className={`ps-price${product.isFree ? ' ps-free' : ''}`}>
              {pickCopy(product.price, activeLocale)}
            </span>
            <div className="ps-card-footer-actions">
              {product.panel === 'grantha' && product.internalPath && (
                <button
                  className={`ps-share-btn${copiedId === product.id ? ' ps-share-copied' : ''}`}
                  title={copiedId === product.id
                    ? (activeLocale === 'en' ? 'Copied!' : 'कॉपी हो गया!')
                    : (activeLocale === 'en' ? 'Copy shareable link' : 'लिंक कॉपी करें')}
                  aria-label={activeLocale === 'en' ? 'Share' : 'साझा करें'}
                  onClick={(e) => { e.stopPropagation(); void handleShare(product); }}
                >
                  {copiedId === product.id ? '✓' : '⎘'}
                </button>
              )}
              {product.status === 'available' && product.internalPath ? (
                <button className="ps-cta" onClick={(e) => { e.stopPropagation(); openProduct(product); }}>
                  {product.cta ? pickCopy(product.cta, activeLocale) : activeLocale === 'en' ? 'Open' : 'खोलें'}
                </button>
              ) : product.status === 'available' && product.buyLink ? (
                <a
                  href={product.buyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ps-cta"
                  onClick={(e) => e.stopPropagation()}
                >
                  {product.cta ? pickCopy(product.cta, activeLocale) : activeLocale === 'en' ? 'Open' : 'खोलें'}
                </a>
              ) : product.status === 'pending' ? (
                <span className="ps-pending">{t('products.badge.preorder') || (activeLocale === 'en' ? 'Coming soon' : 'शीघ्र आ रहा है')}</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ps-showcase">
      <header className="ps-header" id="products-top">
        <ShareableHeading id="products-top" as="h1">{t('nav.products') || PAGE_COPY[activeLocale].title}</ShareableHeading>
        <p className="ps-subtitle">{PAGE_COPY[activeLocale].subtitle}</p>
      </header>

      {/* Panel tabs */}
      <div className="ps-tabs" role="tablist">
        {PANELS.map(p => (
          <button
            key={p.id}
            role="tab"
            aria-selected={activePanel === p.id}
            className={`ps-tab${activePanel === p.id ? ' ps-tab-active' : ''}`}
            onClick={() => setActivePanel(p.id)}
          >
            <span className="ps-tab-icon">{p.icon}</span>
            <span className="ps-tab-label">{activeLocale === 'en' ? p.en : p.hi}</span>
            <span className="ps-tab-count">{panelItems[p.id].length}</span>
          </button>
        ))}
      </div>

      {/* Panel content */}
      <div className="ps-panel" role="tabpanel">
        <div className={`ps-grid ps-grid-${activePanel}`}>
          {panelItems[activePanel].map(renderCard)}
        </div>
      </div>
    </div>
  );
}
